// BattleScene: owns one battle — the World, the fixed-timestep GameLoop, the camera,
// renderer, HUD, and input. The loop advances the sim at 25tps and renders interpolated
// at display rate. On a decided battle it calls onEnd with the result.

import { GameLoop, TICK_DT } from "../engine/loop";
import type { BattlePhase, Faction, WorldConfig } from "../sim/types";
import { createBattle, scriptedFirefight } from "../sim/setup";
import type { World } from "../sim/world";
import { Camera, ICamera } from "../render/camera";
import { IWorldRenderer, Renderer, ViewState } from "../render/renderer";
import { PcRenderer } from "../render3d/pc-renderer";
import { WorldOverlay } from "../render3d/overlay";
import { Hud } from "../render/hud";
import { BattleInput, newUi, Ui } from "./input";

/** Renderer backend: PlayCanvas 3D by default; `?renderer=2d` keeps the legacy Canvas 2D. */
function rendererMode(): "2d" | "3d" {
  try {
    return new URLSearchParams(window.location.search).get("renderer") === "2d" ? "2d" : "3d";
  } catch {
    return "3d";
  }
}

export interface BattleResult {
  phase: BattlePhase;
  winner: Faction | null;
  world: World;
}

export class BattleScene {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cam: ICamera;
  private renderer: IWorldRenderer;
  private overlay: WorldOverlay | null = null;
  private is3d: boolean;
  private hud: Hud;
  private input: BattleInput;
  private loop: GameLoop;
  private ui: Ui;
  world: World;
  private lastRender = 0;
  private ended = false;
  private onEnd: (r: BattleResult) => void;
  spectate: boolean;

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, playerFaction: Faction, opts: { spectate?: boolean; demo?: boolean }, onEnd: (r: BattleResult) => void) {
    this.canvas = canvas;
    this.ctx = ctx;
    this.onEnd = onEnd;
    this.spectate = !!opts.spectate;

    const cfg: WorldConfig = {
      seed: (Date.now() & 0xffffff) >>> 0,
      playerFaction,
      ticketStart: 600,
      ticketDrainPerSec: 2.2,
      slotCap: 20,
      fastMode: false,
    };
    this.world = createBattle(cfg);
    if (opts.demo) scriptedFirefight(this.world);
    if (this.spectate) this.world.armies[playerFaction].isAI = true;

    this.is3d = rendererMode() === "3d";
    this.ui = newUi(playerFaction);
    if (this.is3d) {
      const mount = (canvas.parentElement as HTMLElement) ?? document.body;
      const pcr = new PcRenderer(mount);
      this.renderer = pcr;
      this.cam = pcr.camera;
      this.overlay = new WorldOverlay(ctx);
    } else {
      const cam = new Camera();
      this.cam = cam;
      this.renderer = new Renderer(ctx, cam);
    }
    this.hud = new Hud(ctx);
    this.loop = new GameLoop({ tick: () => this.tick(), render: (a) => this.render(a) });
    this.input = new BattleInput(canvas, this.cam, this.hud, this.loop, this.ui, () => this.world);

    (window as unknown as { __battle: BattleScene }).__battle = this;
    this.resize();
    this.ui.hoverScreen = { x: this.cam.vw / 2, y: this.cam.vh / 2 };
    // start focused on the player's base (or center for demo/spectate)
    const focus = opts.demo ? { x: this.world.map.width / 2, y: this.world.map.height / 2 } : this.world.map.hq[playerFaction];
    this.cam.centerOn({ x: focus.x + (playerFaction === "legion" ? 240 : -240), y: focus.y });
  }

  start(): void {
    this.lastRender = performance.now();
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
    this.input.detach();
    this.renderer.destroy?.();
  }

  resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.cam.setViewport(w, h, dpr);
    this.cam.clampToMap(this.world.map.width, this.world.map.height);
    this.renderer.resize?.();
  }

  private tick(): void {
    if (this.ended) return;
    this.world.tick(TICK_DT);
    if (this.world.phase !== "playing" && !this.ended) {
      this.ended = true;
      // linger a moment on the final frame, then report
      setTimeout(() => this.onEnd({ phase: this.world.phase, winner: this.world.winner, world: this.world }), 1400);
    }
  }

  private render(alpha: number): void {
    const now = performance.now();
    let dt = (now - this.lastRender) / 1000;
    this.lastRender = now;
    if (dt > 0.1) dt = 0.1;
    this.input.update(dt);
    this.ui.animTime += dt;

    const ctx = this.ctx;
    ctx.setTransform(this.cam.dpr, 0, 0, this.cam.dpr, 0, 0);
    if (this.is3d) {
      // transparent overlay — the WebGL scene shows through behind the HUD canvas
      ctx.clearRect(0, 0, this.cam.vw, this.cam.vh);
    } else {
      ctx.fillStyle = "#0a0908";
      ctx.fillRect(0, 0, this.cam.vw, this.cam.vh);
    }

    const overChrome = this.hud.overChrome(this.ui.hoverScreen.x, this.ui.hoverScreen.y, this.cam.vw, this.cam.vh);
    const view: ViewState = {
      alpha,
      animTime: this.ui.animTime,
      playerFaction: this.ui.playerFaction,
      hoverWorld: overChrome ? null : this.ui.hoverWorld,
      selectionBox: this.ui.selectionBoxScreen,
      showMovePreview: !this.spectate && this.ui.selected.size > 0 && !overChrome,
      facingDrag: this.ui.facingDrag
        ? { from: this.cam.screenToWorld(this.ui.facingDrag.fromScreen), to: this.cam.screenToWorld(this.ui.facingDrag.toScreen) }
        : null,
    };
    this.renderer.draw(this.world, view);
    // In 3D the flat gameplay UI (bars/labels/previews/text) is a screen-space overlay.
    if (this.overlay) {
      this.overlay.draw(this.world, view, this.cam);
      if (view.selectionBox) this.drawSelectionBox(view.selectionBox);
    }
    if (!this.spectate) {
      this.hud.draw(this.world, {
        playerFaction: this.ui.playerFaction,
        selected: this.ui.selected,
        vw: this.cam.vw,
        vh: this.cam.vh,
        animTime: this.ui.animTime,
        message: this.ui.attackMoveArmed ? "Attack-move: click a destination" : this.ui.abilityTargeting ? "Ability: click a target" : this.ui.message,
      });
    } else {
      this.drawSpectateBanner();
    }
  }

  private drawSelectionBox(box: { a: { x: number; y: number }; b: { x: number; y: number } }): void {
    const ctx = this.ctx;
    const x = Math.min(box.a.x, box.b.x);
    const y = Math.min(box.a.y, box.b.y);
    const w = Math.abs(box.a.x - box.b.x);
    const h = Math.abs(box.a.y - box.b.y);
    ctx.fillStyle = "rgba(120,220,120,0.1)";
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = "rgba(120,220,120,0.8)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x, y, w, h);
  }

  private drawSpectateBanner(): void {
    const ctx = this.ctx;
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(this.cam.vw / 2 - 140, 12, 280, 30);
    ctx.fillStyle = "#ffd88a";
    ctx.font = "700 14px Trebuchet MS";
    ctx.textAlign = "center";
    ctx.fillText("DEMO FIREFIGHT — spectating", this.cam.vw / 2, 32);
    ctx.textAlign = "left";
  }
}
